/**
 * Loads modular game content from /public/data at runtime. Each category is an
 * index-driven folder: data/<cat>/index.json lists ids, each id is a
 * data/<cat>/<id>.json file. Adding content = drop a file + an index entry +
 * refresh (no rebuild) — the foundation of the moddable design.
 *
 * After assembly, validateGameData() enforces "these props are allowed" with
 * clear file:field errors, so bad mod data fails AT LOAD instead of crashing
 * deep in the sim.
 */
import { Registry } from "./registry";
import type {
  BuildingDef,
  CivDef,
  GameData,
  MapDef,
  ResourceDef,
  ResourceNodeDef,
  UnitDef,
} from "./schema";
import { TERRAIN_TYPES, type TerrainType } from "./schema";

const BASE = import.meta.env.BASE_URL || "/";

async function fetchJSON<T>(path: string): Promise<T> {
  const res = await fetch(BASE + "data/" + path);
  if (!res.ok) throw new Error(`Failed to load data/${path}: ${res.status}`);
  return (await res.json()) as T;
}

/** Load an index-driven category into an id -> def map. */
async function loadCategory<T>(dir: string, key: string): Promise<Record<string, T>> {
  const index = await fetchJSON<Record<string, string[]>>(`${dir}/index.json`);
  const ids = index[key] ?? [];
  const items = await Promise.all(ids.map((id) => fetchJSON<T>(`${dir}/${id}.json`)));
  const out: Record<string, T> = {};
  ids.forEach((id, i) => { out[id] = items[i]; });
  return out;
}

export async function loadData(): Promise<GameData> {
  const [resources, resourceNodes, units, buildings, civs] = await Promise.all([
    loadCategory<ResourceDef>("resources", "resources"),
    loadCategory<ResourceNodeDef>("resourceNodes", "resourceNodes"),
    loadCategory<UnitDef>("units", "units"),
    loadCategory<BuildingDef>("buildings", "buildings"),
    loadCategory<CivDef>("civs", "civs"),
  ]);

  // maps: the index lists FILENAMES (existing convention)
  const mapIndex = await fetchJSON<{ maps: string[] }>("maps/index.json");
  const mapEntries = await Promise.all(mapIndex.maps.map((m) => fetchJSON<MapDef>("maps/" + m)));
  const maps: Record<string, MapDef> = {};
  for (const m of mapEntries) maps[m.id] = m;

  const data: GameData = { resources, resourceNodes, units, buildings, civs, maps };
  validateGameData(data);
  return data;
}

const NUM = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

function checkNonNegative(where: string, value: unknown, errors: string[], positive = false): void {
  if (!NUM(value) || (positive ? value <= 0 : value < 0)) {
    errors.push(`${where} must be a ${positive ? "positive" : "non-negative finite"} number`);
  }
}

function checkCost(where: string, cost: unknown, resourceIds: Set<string>, errors: string[]): void {
  if (!cost || typeof cost !== "object" || Array.isArray(cost)) {
    errors.push(`${where}: missing cost`);
    return;
  }
  for (const [resource, amount] of Object.entries(cost)) {
    if (!resourceIds.has(resource)) errors.push(`${where}.cost.${resource} must be a known resource`);
    if (!NUM(amount) || amount < 0) errors.push(`${where}.cost.${resource} must be a non-negative finite number`);
  }
}

function checkUnit(where: string, u: UnitDef, sharedBuildings: Set<string>, resourceIds: Set<string>, errors: string[]): void {
  for (const f of ["class", "hp", "speed", "los", "buildTime", "population", "armor", "trainingBuilding", "name"] as const) {
    if (u[f] === undefined || u[f] === null) errors.push(`${where}: missing required field "${f}"`);
  }
  for (const field of ["hp", "speed", "los", "buildTime", "population", "armor"] as const) {
    if (u[field] !== undefined) checkNonNegative(`${where}.${field}`, u[field], errors, field === "hp");
  }
  checkCost(where, u.cost, resourceIds, errors);
  if (u.trainingBuilding && !sharedBuildings.has(u.trainingBuilding)) {
    // for civ units, sharedBuildings is widened to include the civ's own buildings
    errors.push(`${where}: trainingBuilding "${u.trainingBuilding}" not found`);
  }
}

function checkBuilding(where: string, b: BuildingDef, resourceIds: Set<string>, errors: string[]): void {
  for (const f of ["name", "hp", "los", "buildTime", "size"] as const) {
    if (b[f] === undefined || b[f] === null) errors.push(`${where}: missing required field "${f}"`);
  }
  for (const field of ["hp", "los", "buildTime"] as const) {
    if (b[field] !== undefined) checkNonNegative(`${where}.${field}`, b[field], errors, field === "hp");
  }
  if (b.size !== undefined && (!Array.isArray(b.size) || b.size.length !== 2 || b.size.some((n) => !NUM(n) || n <= 0))) {
    errors.push(`${where}.size must contain two positive finite numbers`);
  }
  checkCost(where, b.cost, resourceIds, errors);
  if (b.gatherResource && !resourceIds.has(b.gatherResource)) {
    errors.push(`${where}: gatherResource "${b.gatherResource}" is not a known resource`);
  }
}

/** Validate referential integrity + required fields. Throws with all errors. */
export function validateGameData(data: GameData): void {
  const errors: string[] = [];
  const reg = new Registry(data);
  const resourceIds = new Set(Object.keys(data.resources));
  const sharedBuildings = new Set(Object.keys(data.buildings));

  for (const [id, b] of Object.entries(data.buildings)) checkBuilding(`buildings/${id}`, b, resourceIds, errors);
  for (const [id, u] of Object.entries(data.units)) checkUnit(`units/${id}`, u, sharedBuildings, resourceIds, errors);

  for (const [id, node] of Object.entries(data.resourceNodes)) {
    if (!resourceIds.has(node.resource)) errors.push(`resourceNodes/${id}.resource "${node.resource}" is not a known resource`);
    for (const field of ["amount", "gatherTime", "perGather"] as const) {
      checkNonNegative(`resourceNodes/${id}.${field}`, node[field], errors, field === "amount");
    }
  }

  for (const [civId, civ] of Object.entries(data.civs)) {
    const known = new Set([...sharedBuildings, ...Object.keys(civ.buildings ?? {})]);
    if (!civ.units || !Object.keys(civ.units).length) errors.push(`civs/${civId}: has no units`);
    for (const uid of Object.keys(civ.units ?? {})) {
      try {
        checkUnit(`civs/${civId}.units.${uid}`, reg.unit(civId, uid), known, resourceIds, errors);
      } catch (e) {
        errors.push(`civs/${civId}.units.${uid}: ${(e as Error).message}`);
      }
    }
    for (const bid of Object.keys(civ.buildings ?? {})) {
      try {
        checkBuilding(`civs/${civId}.buildings.${bid}`, reg.building(civId, bid), resourceIds, errors);
      } catch (e) {
        errors.push(`civs/${civId}.buildings.${bid}: ${(e as Error).message}`);
      }
    }
  }

  for (const [mid, m] of Object.entries(data.maps)) {
    if (!Number.isInteger(m.width) || m.width <= 0) errors.push(`maps/${mid}.width must be a positive integer`);
    if (!Number.isInteger(m.height) || m.height <= 0) errors.push(`maps/${mid}.height must be a positive integer`);
    checkCost(`maps/${mid}`, m.startingResources, resourceIds, errors);
    if (!NUM(m.terrain?.seed)) errors.push(`maps/${mid}.terrain.seed must be a finite number`);
    if (m.terrain.base !== undefined && !TERRAIN_TYPES.includes(m.terrain.base as TerrainType)) {
      errors.push(`maps/${mid}: terrain base "${m.terrain.base}" must be a known terrain type`);
    }
    for (const feature of m.terrain.features ?? []) {
      if (!TERRAIN_TYPES.includes(feature.type as TerrainType)) {
        errors.push(`maps/${mid}: terrain feature "${feature.type}" must be a known terrain type`);
      }
      if (!Number.isInteger(feature.count) || feature.count < 0) {
        errors.push(`maps/${mid}: terrain feature count must be a non-negative integer`);
      }
      if (!Number.isInteger(feature.minSize) || feature.minSize < 1) {
        errors.push(`maps/${mid}: terrain feature minSize must be a positive integer`);
      }
      if (!Number.isInteger(feature.maxSize) || feature.maxSize < feature.minSize) {
        errors.push(`maps/${mid}: terrain feature maxSize must be an integer at least minSize`);
      }
    }
    for (const p of m.players) {
      if (!data.civs[p.civ]) errors.push(`maps/${mid}: player ${p.id} references unknown civ "${p.civ}"`);
    }
    for (const entity of m.startingEntities) {
      const player = m.players.find((p) => p.id === entity.player);
      if (!player) {
        errors.push(`maps/${mid}: starting entity references unknown player ${entity.player}`);
      } else if (entity.kind === "building") {
        try { reg.building(player.civ, entity.type); } catch (e) { errors.push(`maps/${mid}: starting building "${entity.type}": ${(e as Error).message}`); }
      } else if (data.civs[player.civ] && !reg.roleUnit(player.civ, entity.type)) {
        errors.push(`maps/${mid}: starting unit role "${entity.type}" not found for civ "${player.civ}"`);
      }
    }
    for (const recipe of [...(m.terrain.startingResources ?? []), ...(m.terrain.scatteredResources ?? [])]) {
      if (!data.resourceNodes[recipe.type]) errors.push(`maps/${mid}: terrain resource "${recipe.type}" not found`);
      if (!Number.isInteger(recipe.count) || recipe.count < 0) errors.push(`maps/${mid}: terrain resource count must be a non-negative integer`);
      if ("distance" in recipe && (!Number.isInteger(recipe.distance) || recipe.distance < 0)) errors.push(`maps/${mid}: terrain resource distance must be a non-negative integer`);
      if ("perCluster" in recipe && (!Number.isInteger(recipe.perCluster) || recipe.perCluster < 1)) errors.push(`maps/${mid}: terrain resource perCluster must be a positive integer`);
    }
  }

  if (errors.length) {
    throw new Error(`Invalid game data (${errors.length} ${errors.length === 1 ? "error" : "errors"}):\n  - ${errors.join("\n  - ")}`);
  }
}
